import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  FileText,
  TrendingUp,
  FileCheck,
  ShieldCheck,
  RefreshCw,
  Edit,
  FileWarning,
  Bell,
  CheckCircle,
} from 'lucide-react';

const GSTServices = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = [
    {
      id: 'registration',
      icon: <FileText size={40} />,
      title: 'GST Registration',
      description:
        'Complete assistance for GST registration for your business. We handle all documentation and ensure quick registration with the GST portal.',
      features: ['New Registration', 'Composition Scheme', 'Document Preparation', 'Portal Assistance'],
    },
    {
      id: 'return-filing',
      icon: <TrendingUp size={40} />,
      title: 'GST Return Filing',
      description:
        'Timely and accurate GST return filing services for GSTR-1, GSTR-3B, GSTR-4, and annual returns. Never miss a deadline again.',
      features: ['GSTR-1 Filing', 'GSTR-3B Filing', 'GSTR-4 Filing', 'Monthly/Quarterly Returns'],
    },
    {
      id: 'lut-form',
      icon: <FileCheck size={40} />,
      title: 'GST LUT Form',
      description:
        'Letter of Undertaking (LUT) filing for businesses engaged in export of goods or services without payment of IGST.',
      features: ['LUT Application', 'LUT Renewal', 'Export Documentation', 'Compliance Check'],
    },
    {
      id: 'audit',
      icon: <ShieldCheck size={40} />,
      title: 'GST Audit',
      description:
        'Comprehensive GST audit services to ensure compliance and identify potential issues before they become problems.',
      features: ['Compliance Audit', 'Reconciliation', 'Error Detection', 'Audit Report'],
    },
    {
      id: 'revocation',
      icon: <RefreshCw size={40} />,
      title: 'GST Revocation',
      description:
        'Assistance with GST registration revocation and cancellation. We help restore cancelled GST registrations.',
      features: ['Cancellation Revocation', 'Appeal Filing', 'Document Support', 'Follow-up'],
    },
    {
      id: 'amendment',
      icon: <Edit size={40} />,
      title: 'GST Amendment',
      description:
        'Amendment of GST registration details including business name, address, authorized signatory, and other particulars.',
      features: ['Core Fields Amendment', 'Non-Core Fields', 'Document Update', 'Verification'],
    },
    {
      id: 'gstr10',
      icon: <FileWarning size={40} />,
      title: 'GSTR 10',
      description:
        'Final return filing (GSTR-10) for businesses whose GST registration has been cancelled or surrendered.',
      features: ['Final Return', 'Stock Details', 'Input Credit', 'Closure Compliance'],
    },
    {
      id: 'notice-reply',
      icon: <Bell size={40} />,
      title: 'GST Notice Reply',
      description:
        'Expert assistance in responding to GST notices, show cause notices, and demand orders from tax authorities.',
      features: ['Notice Analysis', 'Reply Drafting', 'Document Support', 'Representation'],
    },
    {
      id: 'gstr9c',
      icon: <CheckCircle size={40} />,
      title: 'GSTR 9C',
      description:
        'GST annual reconciliation statement and audit certification by a Chartered Accountant or Cost Accountant.',
      features: ['Reconciliation', 'Audit Certification', 'Annual Return', 'Compliance Check'],
    },
  ];

  return (
    <div className="pt-32" data-testid="gst-services-page">
      {/* Hero Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight" data-testid="gst-hero-title">
              GST Services
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Comprehensive GST solutions for your business. From registration to compliance, we handle it all with expertise and precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="gst-services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all"
                data-testid={`gst-service-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-[#2D3C92] flex-shrink-0 floating">{service.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#0F172A] mb-3">{service.title}</h3>
                        <p className="text-base text-slate-600 leading-relaxed">{service.description}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0F172A] mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="text-[#10B981] flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-slate-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2D3C92]" data-testid="gst-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Need Help with GST Compliance?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Contact us today and let our experts handle your GST requirements with accuracy and efficiency.
            </p>
            <a
              href="/contact"
              className="bg-[#10B981] text-white hover:bg-[#047857] px-8 py-4 rounded-full font-bold text-lg inline-block transition-all shadow-xl"
              data-testid="gst-cta-button"
            >
              Get Started
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GSTServices;